import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ensureFreshSpaceGuestToken,
  getSpaceFileDownloadUrl,
  getSpaceFilePreviewUrl,
  getSpaceFileThumbnailUrl,
  getSpaceZipUrl,
  refreshSpaceGuestTokenNow,
} from "../api";
import { PublishedSpaceResolution, ShareLinkFile } from "../entities";
import { previewImageRequestSize } from "../pages/preview/Preview";
import {
  isInlinePreviewMime,
  parentSegments,
  pathSegments,
} from "../utils/fileFormat";
import { useFileSelection } from "./useFileSelection";
import { useSpaceTree } from "./useSpaceTree";

/** MIME type the API uses for directory entries in a space's flat file list. */
const DIRECTORY_MIME = "inode/directory";

/**
 * Single query param addressing the open navigation target by uid (per the
 * epic): a folder uid opens that folder, a file uid opens the file's parent
 * folder and highlights it, and no param (or an unresolvable one) opens the
 * root. Uids are URL-safe and rename-proof, so there is nothing to encode and a
 * shared link survives folder renames/moves.
 */
const TARGET_PARAM = "target";

/** The view a `?target=<uid>` resolves to. */
type ResolvedTarget = {
  /** Directory to open (folder-name segments; `[]` = root). */
  path: string[];
  /** File to highlight, by uid (only for a file target); `null` otherwise. */
  focusedKey: string | null;
  /** UID of the open folder itself (`null` at the root) — the browsing target. */
  folderUid: string | null;
};

/**
 * Resolves a `?target=<uid>` against the flat entry list, using `uidByPath`
 * (folder full path → directory-entry uid) so a file target can report the uid
 * of the parent folder it lands in. An absent or unresolvable target opens the
 * root; a folder target opens that folder; a file target opens its parent and
 * highlights the file.
 */
const resolveTarget = (
  files: ShareLinkFile[],
  uidByPath: Map<string, string>,
  target: string | null
): ResolvedTarget => {
  const root: ResolvedTarget = { path: [], focusedKey: null, folderUid: null };
  if (!target) return root;
  const entry = files.find((f) => f.uid === target);
  if (!entry) return root;
  if (entry.mimeType === DIRECTORY_MIME) {
    return {
      path: pathSegments(entry.path),
      focusedKey: null,
      folderUid: entry.uid,
    };
  }
  const parent = parentSegments(entry.path);
  return {
    path: parent,
    focusedKey: entry.uid,
    folderUid: uidByPath.get(parent.join("/")) ?? null,
  };
};

/**
 * Trigger a browser download of `href` via a hidden iframe.
 *
 * The download endpoints are cross-origin (the Drive API), so an `<a download>`
 * click is treated as a *navigation*, not an in-page download — and firing
 * several synchronously only ever performs one (each navigation supersedes the
 * previous), which is why "download N files" only saved one. Each iframe is its
 * own navigation context, so N iframes reliably start N downloads; an
 * `attachment` response downloads without touching the parent page, and the
 * guest cookie (SameSite=None) still rides along. The iframe is removed once the
 * download has had time to start (removing it doesn't cancel a started download).
 */
const triggerDownload = (href: string) => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.display = "none";
  iframe.src = href;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    iframe.parentNode?.removeChild(iframe);
  }, 60000);
};

/**
 * Shared state + actions for a published-space browser, factored out of the
 * page so the {@link DesktopSpaceBrowser} layout drives one model: the current
 * directory path, the resolved folder/file listing, a space-wide selection, the
 * (guest-authenticated) thumbnail/preview URLs and the download.
 *
 * The whole view (open directory + highlighted file) is derived from the URL's
 * single `?target=<uid>` and is the source of truth, so navigating pushes a
 * history entry and browser Back steps up to the previous target. A folder uid
 * opens that folder; a file uid opens the file's parent and highlights it; no
 * target opens the root. The preview overlay is local state (not in the URL),
 * but while it's open the target points at the previewed file so the address bar
 * stays shareable.
 */
export function useSpaceBrowser(
  spaceUid: string,
  data: PublishedSpaceResolution
) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Folder full path → its directory-entry uid, so navigation can address a
  // folder by its stable uid and a file target can name its parent folder.
  const uidByPath = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of data.files) {
      if (f.mimeType === DIRECTORY_MIME) {
        map.set(pathSegments(f.path).join("/"), f.uid);
      }
    }
    return map;
  }, [data.files]);

  // The open directory + highlight are derived from `?target=`, so browser
  // Back/Forward simply change the URL and this recomputes — no path state.
  const target = searchParams.get(TARGET_PARAM);
  const resolved = useMemo(
    () => resolveTarget(data.files, uidByPath, target),
    [data.files, uidByPath, target]
  );
  const path = resolved.path;

  // Preview overlay (double-click / kebab "Preview") — local, not in the URL.
  const [previewFile, setPreviewFileState] = useState<ShareLinkFile | null>(
    null
  );
  // The highlighted item key: seeded from a file target on load, then also set
  // interactively (single-click folder focus). A folder target opens the folder
  // rather than highlighting it, so it seeds no highlight.
  const [focusedKey, setFocusedKey] = useState<string | null>(
    () => resolved.focusedKey
  );

  const { folders, files, exists } = useSpaceTree(data.files, path);

  // Write the URL target. Pushes a history entry by default (real navigation);
  // pass `{ replace: true }` for corrections and preview toggles. `null` → root.
  const setTarget = useCallback(
    (uid: string | null, opts?: { replace?: boolean }) => {
      const params = new URLSearchParams(searchParams);
      if (uid) params.set(TARGET_PARAM, uid);
      else params.delete(TARGET_PARAM);
      setSearchParams(params, { replace: opts?.replace ?? false });
    },
    [searchParams, setSearchParams]
  );

  // An unresolvable target (renamed-away or hand-edited link) cleanly drops to
  // the root: strip the dangling param once on load.
  useEffect(() => {
    if (target && !data.files.some((f) => f.uid === target)) {
      setTarget(null, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Navigate to a directory (by folder-name segments). Pushes a history entry by
   * default so Back returns to the previous folder; pass `{ replace: true }` for
   * non-navigational corrections. Clears the preview and highlight.
   */
  const navigateTo = useCallback(
    (next: string[], opts?: { replace?: boolean }) => {
      setTarget(uidByPath.get(next.join("/")) ?? null, {
        replace: opts?.replace,
      });
      setPreviewFileState(null);
      setFocusedKey(null);
    },
    [setTarget, uidByPath]
  );

  // Open/close the preview overlay. While open, the URL target points at the
  // previewed file (shareable); closing it points the target back at the current
  // folder. Replace — a preview toggle isn't navigation.
  const setPreviewFile = useCallback(
    (file: ShareLinkFile | null) => {
      setPreviewFileState(file);
      setTarget(file ? file.uid : resolved.folderUid, { replace: true });
    },
    [setTarget, resolved.folderUid]
  );

  // A stale/removed deep path falls back to the root (replace — not a real nav).
  useEffect(() => {
    if (!exists && path.length > 0) navigateTo([], { replace: true });
  }, [exists, path.length, navigateTo]);

  // Selection spans the whole space (persists across folders) so a multi-file
  // download can gather picks from several folders.
  const allUids = useMemo(
    () =>
      data.files.filter((f) => f.mimeType !== DIRECTORY_MIME).map((f) => f.uid),
    [data.files]
  );
  const selection = useFileSelection(allUids);

  // Card thumbnails use the pre-generated thumbnail endpoint, loaded by the
  // shared FileCard in a native <img> (ETag revalidation, no cache-busting
  // param). FileCard falls back to the file-type icon when a thumbnail is absent.
  const buildThumbnailUrl = useCallback(
    (file: ShareLinkFile) => getSpaceFileThumbnailUrl(spaceUid, file.uid),
    [spaceUid]
  );

  // The URL the preview overlay loads for a file: media / PDF / text are served
  // inline (played/embedded directly), everything else gets a rendered preview
  // image sized to the viewport. The inline-vs-render decision is shared with the
  // share-link viewer (see {@link isInlinePreviewMime}) so they can't drift.
  const buildPreviewUrl = useCallback(
    (file: ShareLinkFile) =>
      isInlinePreviewMime(file.mimeType)
        ? getSpaceFileDownloadUrl(spaceUid, file.uid, { inline: true })
        : getSpaceFilePreviewUrl(spaceUid, file.uid, previewImageRequestSize()),
    [spaceUid]
  );
  const previewRefreshAuth = useCallback(
    () => refreshSpaceGuestTokenNow(spaceUid),
    [spaceUid]
  );

  // The space's ZIP endpoint archives the whole space (no subset param), so:
  //  - everything selected → a single whole-space ZIP,
  //  - a partial selection → one download per selected file (the share-link
  //    viewer does the same). The browser asks once to allow multiple downloads.
  //
  // CRITICAL: the downloads must fire *synchronously* inside the click's user
  // gesture. Awaiting a token refresh first (or staggering with setTimeout) drops
  // the user-activation, and browsers then block every download after the first —
  // the "only one file downloads" bug. So we trigger them all synchronously and
  // refresh the guest cookie best-effort in the background instead (it's already
  // kept fresh on load + on visibility change).
  const downloadSelected = useCallback(() => {
    const selected = data.files.filter((f) =>
      selection.selectedUids.has(f.uid)
    );
    if (selected.length === 0) return;
    void ensureFreshSpaceGuestToken(spaceUid);
    // Whole selection = whole space → the server ZIP (one download, no client cost).
    if (selected.length === allUids.length) {
      triggerDownload(getSpaceZipUrl(spaceUid));
      return;
    }
    // A subset → one download per file, fired synchronously (see above).
    selected.forEach((file) =>
      triggerDownload(getSpaceFileDownloadUrl(spaceUid, file.uid))
    );
  }, [data.files, selection.selectedUids, allUids.length, spaceUid]);

  // Download the whole space as a single ZIP (the header's "Download entire
  // Space" action) — independent of the current selection.
  const downloadAll = useCallback(() => {
    void ensureFreshSpaceGuestToken(spaceUid);
    triggerDownload(getSpaceZipUrl(spaceUid));
  }, [spaceUid]);

  return {
    path,
    navigateTo,
    previewFile,
    setPreviewFile,
    focusedKey,
    setFocusedKey,
    folders,
    files,
    exists,
    selection,
    allUids,
    buildThumbnailUrl,
    buildPreviewUrl,
    previewRefreshAuth,
    downloadSelected,
    downloadAll,
  };
}
